import React from 'react';
import { PageHeader } from '../components/ui/PageHeader';
import { Section, SectionHeading } from '../components/ui/Section';
import { Prose, ProseParagraph, ProseList } from '../components/ui/Prose';
import { PullQuote, SectionDivider } from '../components/ui/PullQuote';
import { ContinueExploring } from '../components/ui/ContinueExploring';

const ForBuilders = () => {
  return (
    <div>
      <PageHeader 
        title="For Builders"
        subtitle="Building systems that know where they are going"
      />

      {/* A different kind of invitation */}
      <Section>
        <SectionHeading>A different kind of invitation</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Most builders are asked one question: <strong>How fast can we make this work?</strong>
          </ProseParagraph>
          
          <ProseParagraph>
            Coherence OS asks a different question: <strong>What is this system here to create—at the scale it succeeds?</strong>
          </ProseParagraph>
          
          <ProseParagraph>
            This is not a question of intent. It is a question of trajectory.
          </ProseParagraph>
          
          <ProseParagraph>
            Because live, scaling, autonomous systems produce consequences regardless of permission. Coherence OS is for those who want to consciously author those consequences.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* What it means to build with Coherence OS */}
      <Section>
        <SectionHeading>What it means to build with Coherence OS</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Building with Coherence OS doesn't mean slowing down or sacrificing ambition.
          </ProseParagraph>
          
          <ProseParagraph>
            It means building systems that:
          </ProseParagraph>
          
          <ProseList items={[
            "Move quickly without drifting",
            "Scale without losing coherence",
            "Perform without undermining dignity or meaning",
            "Remain aligned to long-term outcomes as complexity increases"
          ]} />
          
          <ProseParagraph>
            This is possible because <strong>direction is structurally governed</strong>—which frees builders to focus on creativity, execution, and innovation without carrying the burden of constantly fixing drift.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* The kind of builders this calls in */}
      <Section>
        <SectionHeading>The kind of builders this calls in</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Coherence OS attracts builders who:
          </ProseParagraph>
          
          <ProseList items={[
            "Care about what their work ultimately shapes",
            "Understand that systems outlive their creators",
            "Want their skill to contribute to something that endures",
            "Feel energized by responsibility at scale"
          ]} />
          
          <ProseParagraph>
            This is not about heroism or sacrifice. It is about <strong>alignment</strong>.
          </ProseParagraph>
          
          <ProseParagraph>
            When direction is clear and governed, building becomes <strong>life-giving</strong>, not exhausting.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* What makes this different from other "mission-driven" work */}
      <Section>
        <SectionHeading>What makes this different from other "mission-driven" work</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Many mission-driven efforts rely on:
          </ProseParagraph>
          
          <ProseList items={[
            "individual vigilance",
            "cultural enforcement",
            "values statements",
            "constant correction"
          ]} />
          
          <ProseParagraph>
            Coherence OS embeds orientation into the system itself—so builders don't have to compensate for structural ambiguity with personal effort.
          </ProseParagraph>
          
          <ProseParagraph>
            This enables:
          </ProseParagraph>
          
          <ProseList items={[
            "Creativity to flourish without chaos",
            "Autonomy without fragmentation",
            "Speed without erosion"
          ]} />
          
          <ProseParagraph>
            Builders can trust the system they are building inside.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* How builders participate */}
      <Section>
        <SectionHeading>How builders participate</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Builders engage with Coherence OS at the level of:
          </ProseParagraph>
          
          <ProseList items={[
            "System design",
            "Product architecture",
            "Organizational structure",
            "Decision logic",
            "Execution frameworks"
          ]} />
          
          <ProseParagraph>
            Always with a clear understanding of:
          </ProseParagraph>
          
          <ProseList items={[
            "What the system is here to create",
            "What tradeoffs are not permitted",
            'What "success" actually means over time'
          ]} />
          
          <ProseParagraph>
            This clarity removes guesswork and aligns teams without coercion.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* Compensation and alignment */}
      <Section>
        <SectionHeading>Compensation and alignment</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Coherence OS is built by aligned people.
          </ProseParagraph>
          
          <ProseParagraph>
            In the early phase:
          </ProseParagraph>
          
          <ProseList items={[
            "Builders participate with equity-first alignment",
            "Ownership reflects contribution and stewardship",
            "Compensation transitions to cash at break-even or defined funding milestones"
          ]} />
          
          <ProseParagraph>
            This is not unpaid labor framed as virtue. It is shared ownership in something designed to last.
          </ProseParagraph>
          
          <ProseParagraph>
            Builders are valued, respected, and treated as <strong>stewards and contributors to a foundational capability</strong>.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* What becomes possible for builders */}
      <Section>
        <SectionHeading>What becomes possible for builders</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            When builders work inside a system with governed direction:
          </ProseParagraph>
          
          <ProseList items={[
            "Decisions become clearer",
            "Tradeoffs become less exhausting",
            "Work regains meaning",
            "Effort compounds instead of dissipating"
          ]} />
          
          <ProseParagraph>
            Builders are not just shipping features; they are shaping <strong>how systems are built at civilization scale</strong>.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* Who this is for */}
      <Section>
        <SectionHeading>Who this is for</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Coherence OS is for builders who:
          </ProseParagraph>
          
          <ProseList items={[
            "Want their work to matter beyond short cycles",
            "Enjoy complexity but value coherence",
            "Are energized by possibility, not weighed down by obligation",
            "Want to build something that humanity will depend on"
          ]} />
          
          <ProseParagraph>
            If you have ever felt the tension between what you <strong>can</strong> build and what the world actually <strong>needs</strong>—Coherence OS resolves that tension structurally.
          </ProseParagraph>
        </Prose>
        
        {/* Continue exploring */}
        <ContinueExploring 
          links={[
            { label: 'What Is Coherence OS', href: '/what-is-coherence-os' },
            { label: 'The Category', href: '/the-category' },
            { label: 'Coherence Metrics', href: '/coherence-metrics' },
          ]}
        />
      </Section>
    </div>
  );
};

export default ForBuilders;
